import { ApolloClient } from "@apollo/client";
import createApolloClient from "./createApolloClient";

interface StoredCredentials {
  uri: string;
  accessToken: string;
  role: string;
}

/**
 * Singleton class that manages Apollo Client instances throughout the application lifecycle.
 *
 * Benefits:
 * - Reuses the same client when credentials haven't changed
 * - Automatically creates new clients when credentials change
 * - Properly cleans up old clients to prevent memory leaks
 * - Preserves WebSocket connections and cache when possible
 *
 * Usage:
 * ```typescript
 * import { apolloManager } from 'src/services/apolloManager';
 *
 * // Get client (will reuse existing if credentials match)
 * const client = apolloManager.getClient(uri, accessToken, role);
 *
 * // Reset client (useful on logout)
 * apolloManager.reset();
 * ```
 */
class ApolloManager {
  private static instance: ApolloManager;
  private client: ApolloClient<any> | null = null;
  private credentials: StoredCredentials | null = null;

  private constructor() {}

  public static getInstance(): ApolloManager {
    if (!ApolloManager.instance) {
      ApolloManager.instance = new ApolloManager();
    }
    return ApolloManager.instance;
  }

  public getClient(
    uri: string,
    accessToken: string,
    role: string
  ): ApolloClient<any> {
    const newCredentials = { uri, accessToken, role };

    // Check if we can reuse the existing client
    if (
      this.client &&
      this.credentials &&
      this.credentialsMatch(newCredentials)
    ) {
      return this.client;
    }

    // Clean up the old client if it exists
    if (this.client) {
      console.info(
        "ApolloManager: Creating new client due to credential changes"
      );
      this.cleanup();
    } else {
      console.info("ApolloManager: Creating initial Apollo client");
    }

    // Create new client and store credentials
    this.client = createApolloClient(uri, accessToken, role);
    this.credentials = newCredentials;

    return this.client;
  }

  public reset(): void {
    console.info("ApolloManager: Resetting Apollo client");
    this.cleanup();
  }

  private credentialsMatch(newCredentials: StoredCredentials): boolean {
    if (!this.credentials) return false;

    return (
      this.credentials.uri === newCredentials.uri &&
      this.credentials.accessToken === newCredentials.accessToken &&
      this.credentials.role === newCredentials.role
    );
  }

  private cleanup(): void {
    if (this.client) {
      // Stop the client to clean up subscriptions and connections
      this.client.stop();
      this.client = null;
    }
    this.credentials = null;
  }
}

// Export singleton instance
export const apolloManager = ApolloManager.getInstance();
